import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Contact.module.css";
import {
  ConsoleLogger,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  DefaultDeviceController,
  LogLevel,
  VoiceFocusDeviceTransformer,
} from "amazon-chime-sdk-js";

import {
  Mic,
  MicOff,
  Dialpad,
  Settings,
  CallEnd,
  ArrowBack,
} from "@mui/icons-material";

export default function Contact() {
  const { t } = useTranslation();
  const [meetingSession, setMeetingSession] = useState(null);
  const [contactId, setContactId] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const [showDialer, setShowDialer] = useState(false);
  const [showDTMFPad, setShowDTMFPad] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState("default");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("default");
  const [vfSupported, setVfSupported] = useState(false);
  const [vfEnabled, setVfEnabled] = useState(true);
  const [vfTransformer, setVfTransformer] = useState(null);
  const [echoEnabled, SetEchoEnabled] = useState(true);
  const [dtmfPressed, setDtmfPressed] = useState([]);
  const [callDuration, setCallDuration] = useState(0);
  const [timeInterval, setTimeInterval] = useState(null);
  const [callEnded, setCallEnded] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const meetingSessionRef = useRef(null);
  const audioRef = useRef(null);
  const dtmfAudioRef = useRef(null);
  const timeIntervalStart = React.useRef(false);

  const START_CALL_API = process.env.REACT_APP_START_CALL_API;
  const SEND_DTMF_API = process.env.REACT_APP_SEND_DTMF_API;
  const WEBRTC_API_KEY = process.env.REACT_APP_WEBRTC_API_KEY;

  const handleCallEnded = () => {
    if (timeInterval) {
      clearInterval(timeInterval);
      setTimeInterval(null);
    }
    setCallDuration(0);
    setIsCallActive(false);
    setCallStatus(t("status.callEnded"));
    setCallEnded(true);

    setTimeout(() => {
      window.close();
    }, 3000);
  };

  const getDnisFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const dnis = params.get("dnis");

    if (!dnis) {
      return null;
    }
    const trimmedDnis = dnis.trim();
    const isValidDnis = /^\+?[0-9]+$/.test(trimmedDnis);

    if (!isValidDnis) {
      return null;
    }
    return trimmedDnis;
  };

  const startWebRTCContact = async (contact) => {
    try {
      const response = await fetch(START_CALL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": WEBRTC_API_KEY,
        },
        body: JSON.stringify({ contact }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to start call");
      }
      const startWebRTCContactResponse = await response.json();
      console.log("startWebRTCContactResponse:: ", startWebRTCContactResponse);

      const connectionData = await startWebRTCContactResponse.ConnectionData;
      console.log("connectionData:: ", connectionData);

      console.log(
        "startWebRTCContactResponse.ContactId:: ",
        startWebRTCContactResponse.ContactId,
      );

      setContactId(startWebRTCContactResponse.ContactId);

      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        connectionData.Meeting,
        connectionData.Attendee,
      );

      meetingSessionConfiguration.enableWebAudio = true;

      const logger = new ConsoleLogger("logger", LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger, {
        enableWebAudio: true,
      });
      const session = new DefaultMeetingSession(
        meetingSessionConfiguration,
        logger,
        deviceController,
      );

      meetingSessionRef.current = session;
      setMeetingSession(session);

      session.audioVideo.addObserver({
        audioVideoDidStop: (sessionStatus) => {
          console.log("Meeting stopped:", sessionStatus);
          handleCallEnded();
        },
      });

      const inDevices = await session.audioVideo.listAudioInputDevices();
      const outDevices = await session.audioVideo.listAudioOutputDevices();
      setMics(inDevices);
      setSpeakers(outDevices);
      setSelectedMicId(inDevices?.[0]?.deviceId || "default");
      setSelectedSpeakerId(outDevices?.[0]?.deviceId || "default");

      const supported = await VoiceFocusDeviceTransformer.isSupported();
      setVfSupported(supported);
      if (supported) {
        const transformer = await VoiceFocusDeviceTransformer.create({
          logger,
        });
        setVfTransformer(transformer);
      }

      await session.audioVideo.startAudioInput("default");
      await session.audioVideo.chooseAudioOutput("default");

      if (audioRef.current) {
        session.audioVideo.bindAudioElement(audioRef.current);
      } else {
        console.error("Audio element not found");
      }

      session.audioVideo.start();

      let agentLeft = false;

      session.audioVideo.realtimeSubscribeToAttendeeIdPresence(
        (attendeeId, present) => {
          if (!present && !attendeeId.includes("customer") && !agentLeft) {
            console.log("Agent left — entering survey phase");
            agentLeft = true;
            setCallStatus(t("status.connectingSurvey"));
          }
        },
      );

      setShowDialer(true);
      const interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
      setTimeInterval(interval);
    } catch (error) {
      console.error("Error starting WebRTC contact:", error);
      setErrorMessage(t("errors.startCallFailed"));
    }
  };

  const toggleMute = () => {
    const session = meetingSessionRef.current;
    if (!session) return;

    if (isMuted) {
      session.audioVideo.realtimeUnmuteLocalAudio();
    } else {
      session.audioVideo.realtimeMuteLocalAudio();
    }
    setIsMuted(!isMuted);
  };

  const stopCall = () => {
    const session = meetingSessionRef.current;
    if (session) {
      session.audioVideo.stop();
    }
    if (timeInterval) {
      clearInterval(timeInterval);
      setTimeInterval(null);
    }
    setCallDuration(0);
    setCallEnded(true);
    setTimeout(() => {
      window.close();
    }, 3000);
  };

  const sendDTMF = async (tone) => {
    console.log("tone:: ", tone);
    console.log("contactId:: ", contactId);

    if (dtmfAudioRef.current) {
      dtmfAudioRef.current.currentTime = 0;
      dtmfAudioRef.current.play().catch(() => {});
    }

    setDtmfPressed((prev) => [...prev, tone]);

    let dtmfBody = {
      contactId: contactId,
      dtmfInput: tone,
    };
    const response = await fetch(SEND_DTMF_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": WEBRTC_API_KEY,
      },
      body: JSON.stringify({ contact: dtmfBody }),
    });
    if (!response.ok) {
      throw new Error("DTMF failed");
    }
    const result = await response.json();
    console.log("DTMF response:", result);
  };

  const applyMic = async (deviceId) => {
    if (!meetingSessionRef.current) return;
    let inputDevice = deviceId || "default";

    if (echoEnabled) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId:
            inputDevice === "default" ? undefined : { exact: inputDevice },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      inputDevice = stream;
    }

    if (vfEnabled && vfTransformer && typeof inputDevice !== "object") {
      const transformed =
        await vfTransformer.createTransformDevice(inputDevice);
      inputDevice = transformed;
    } else if (vfEnabled && vfTransformer && typeof inputDevice === "object") {
      const transformed =
        await vfTransformer.createTransformDevice(inputDevice);
      inputDevice = transformed;
    }
    await meetingSessionRef.current.audioVideo.startAudioInput(inputDevice);
  };

  const applySpeaker = async (deviceId) => {
    if (!meetingSessionRef.current) return;
    try {
      await meetingSessionRef.current.audioVideo.chooseAudioOutput(
        deviceId || "default",
      );
    } catch (e) {
      console.warn(e);
      setErrorMessage(t("errors.switchSpeakerFailed"));
    }
  };

  const onMicChange = async (e) => {
    const id = e.target.value;
    setSelectedMicId(id);
    await applyMic(id);
  };

  const onSpeakerChange = async (e) => {
    const id = e.target.value;
    setSelectedSpeakerId(id);
    await applySpeaker(id);
  };

  const onToggleVoiceFocus = async (e) => {
    if (!vfSupported) return;
    const next = !vfEnabled;
    setVfEnabled(next);
    await applyMic(selectedMicId);
  };

  const onToggleEcho = async () => {
    const next = !echoEnabled;
    SetEchoEnabled(next);
    await applyMic(selectedMicId);
  };

  function getContact() {
    const dnis = getDnisFromUrl();
    if (!dnis) {
      return null;
    }

    return {
      FirstName: "Customer",
      DNIS: dnis,
      WebRTCType: "Audio",
    };
  }

  React.useEffect(() => {
    if (timeIntervalStart.current) {
      return;
    }
    const contact = getContact();
    if (!contact) return;
    timeIntervalStart.current = true;
    startWebRTCContact(contact);
  }, []);

  return (
    <div className={styles.shell}>
      <audio ref={audioRef} className={styles.audio} autoPlay />
      <audio
        ref={dtmfAudioRef}
        src="./dtmf-pad-tone.mp3"
        className={styles.audio}
      ></audio>
      {!callEnded ? (
        <div className={styles.widgetCard}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>{t("header.help")}</div>
          </div>

          <div
            className={`${styles.panelWrapper} ${
              showDTMFPad ? styles.showKeypad : ""
            } ${showPrefs ? styles.showPrefs : ""}`}
          >
            <div className={styles.panelTrack} aria-live="polite">
              <div
                className={styles.mainPanel}
                aria-hidden={showDTMFPad ? "true" : "false"}
              >
                <div className={styles.body}>
                  {errorMessage && (
                    <div className={styles.errorBanner}>{errorMessage}</div>
                  )}
                  <div className={styles.sectionLabel}>{t("status.activeCall")}</div>

                  <div className={styles.callTimer}>
                    {String(Math.floor(callDuration / 60)).padStart(2, "0")}:{" "}
                    {String(callDuration % 60).padStart(2, "0")}
                  </div>

                  <div className={styles.actionGrid}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={toggleMute}
                    >
                      <span className={styles.iconCircle}>
                        {isMuted ? (
                          <MicOff
                            fontSize="medium"
                            style={{ color: "#b00020" }}
                          />
                        ) : (
                          <Mic fontSize="medium" />
                        )}
                      </span>
                      <span className={styles.actionLabel}>
                        {isMuted ? t("actions.unmute") : t("actions.mute")}
                      </span>
                    </button>

                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={() => {
                        setShowDTMFPad(true);
                        setShowPrefs(false);
                      }}
                    >
                      <span className={styles.iconCircle}>
                        <Dialpad fontSize="medium" />
                      </span>
                      <span className={styles.actionLabel}>{t("actions.keypad")}</span>
                    </button>

                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={() => {
                        setShowPrefs(true);
                        setShowDTMFPad(false);
                      }}
                    >
                      <span className={styles.iconCircle}>
                        <Settings fontSize="medium" />
                      </span>
                      <span className={styles.actionLabel}>{t("actions.preferences")}</span>
                    </button>
                  </div>

                  <div className={styles.endWrap}>
                    <button
                      type="button"
                      className={styles.endCall}
                      onClick={stopCall}
                    >
                      <CallEnd fontSize="small" />
                      {t("actions.endCall")}
                    </button>
                  </div>
                </div>
              </div>

              <div
                className={styles.keypadPanel}
                aria-hidden={showDTMFPad ? "false" : "true"}
              >
                <div className={styles.keypadHeader}>
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => setShowDTMFPad(false)}
                    aria-label={t("actions.back")}
                  >
                    <ArrowBack fontSize="small" />
                  </button>
                </div>
                <div className={styles.dtmfDisplay}>
                  {dtmfPressed.length > 0 ? dtmfPressed.join(" ") : "-"}
                </div>

                <div className={styles.dtmfGrid}>
                  {[
                    ["1", ""],
                    ["2", "ABC"],
                    ["3", "DEF"],
                    ["4", "GHI"],
                    ["5", "JKL"],
                    ["6", "MNO"],
                    ["7", "PQRS"],
                    ["8", "TUV"],
                    ["9", "WXYZ"],
                    ["*", ""],
                    ["0", "+"],
                    ["#", ""],
                  ].map(([digit, letters]) => (
                    <button
                      key={digit}
                      type="button"
                      className={styles.dtmfBtn}
                      onClick={() => sendDTMF(digit)}
                    >
                      <span className={styles.digit}>{digit}</span>
                      <span className={styles.letters}>{letters}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div
                className={styles.prefsPanel}
                aria-hidden={showPrefs ? "false" : "true"}
              >
                <div className={styles.keypadHeader}>
                  <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => setShowPrefs(false)}
                    aria-label={t("actions.back")}
                  >
                    <ArrowBack fontSize="small" />
                  </button>
                  <div className={styles.dtmfTitle}>{t("actions.preferences")}</div>
                </div>
                <div className={styles.prefsBody}>
                  <label className={styles.fieldLabel}>{t("preferences.microphone")}</label>
                  <select
                    className={styles.select}
                    value={selectedMicId}
                    onChange={onMicChange}
                  >
                    {mics.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <label className={styles.fieldLabel}>{t("preferences.speaker")}</label>
                  <select
                    className={styles.select}
                    value={selectedSpeakerId}
                    onChange={onSpeakerChange}
                  >
                    {speakers.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>

                  <div className={styles.toggleRow}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={!vfSupported}
                        checked={vfEnabled}
                        onChange={onToggleVoiceFocus}
                      />
                      <span>{t("preferences.enableVoiceFocus")}</span>
                    </label>
                  </div>

                  <div className={styles.toggleRow}>
                    <label>
                      <input
                        type="checkbox"
                        checked={echoEnabled}
                        onChange={onToggleEcho}
                      />
                      <span>{t("preferences.enableEchoReduction")}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.widgetCard}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>{t("header.thanks")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
